import { inject, Injectable } from "@angular/core";

import { Decoration, DecorationSet, EditorView } from "@codemirror/view";
import { Extension, Range } from "@codemirror/state";

import { CodeEditorHighlightService } from "@controls/text-editors/services/code-editor-highlight.service";


@Injectable()
export class WidgetHtmlCodeEditorHighlightService {

    private highlightService: CodeEditorHighlightService = inject(CodeEditorHighlightService);

    public getDirectivesHighlightExtension(): Extension {
        return this.highlightService.getHighlightPlugin(view => this.highlightDirectives(view));

    }

    private highlightDirectives(view: EditorView): DecorationSet {
        // Collect all decorations from all visible ranges
        const decorations: Range<Decoration>[] = [];

        // Pattern to match directive names (@eval, @query, @widget, etc.)
        const regex: RegExp = /@[A-Za-z_]+\s*(?=[({])/g;

        const directiveClass: string = "html-directive";

        // For each visible range, compute decorations
        view.visibleRanges.forEach(range => {

            // Get the text content of the visible range
            const text: string = view.state.doc.sliceString(range.from, range.to);

            // Find all directive matches
            for (let match: RegExpExecArray = regex.exec(text); match !== null; match = regex.exec(text)) {

                // Find the position after the directive name.
                let position: number = match.index + match[0].length;

                // Add the directive name decorator.
                const start: number = range.from + match.index;
                const end: number = range.from + position;

                decorations.push(Decoration.mark({ class: directiveClass }).range(start, end));

                while (position < text.length && /\s/.test(text[position])) position++;

                if (position >= text.length) continue;

                // Check if this is the start of a parenthesis or brace.
                if (text[position] !== "(" && text[position] !== "{") continue;

                // Handle the first pair of bracket/parenthesis
                const decorators: Range<Decoration>[] = this.getInnerDecorators(text, position, range.from);

                if (!decorators?.length) continue;

                decorations.push(...decorators);

                // If the first bracket was a parenthesis, check for a brace after it.
                if (text[position] !== "(") continue;

                // Skip whitespaces after the closing parenthesis.
                position = decorators[decorators.length - 1].to;

                while (position < text.length && /\s/.test(text[position])) position++;

                // Check if this is the start of a brace.
                if (text[position] !== "{") continue;

                // Handle the second pair of bracket/parenthesis
                const [opening, closing] = this.getInnerDecorators(text, position, range.from);

                if (!opening || !closing) continue;

                decorations.push(opening, closing);
            }
        });

        // Sort decorations by their position to satisfy codemirror requirements.
        decorations.sort((first, second) => {

            // If their 'from' positions are different, sort by them.
            if (first.from !== second.from) return first.from - second.from;

            // Otherwise, sort by 'to' positions.
            return first.to - second.to;
        });

        return Decoration.set(decorations);
    }

    private getInnerDecorators(text: string, position: number, from: number): Range<Decoration>[] {
        if (!text || position >= text.length) return [];

        const opening: string = text[position];
        const closing: string = text[position] === "(" ? ")" : "}";

        const decorators: Range<Decoration>[] = [];

        const colonClass: string = "html-directive-colon";
        const bracketClass: string = "html-directive-bracket";

        // Start from the opening character.
        for (let index: number = position, depth: number = 0; index < text.length; index++) {

            if (text[index] === opening) depth++;
            if (text[index] === closing) depth--;

            // If current position is not inside this directive arguments, continue.
            if (depth > 1 && text[position] !== "(") continue;

            if (text[index] === "," || text[index] === ";") {
                // Highlight colons if they correspond to this directive arguments.
                decorators.push(Decoration.mark({ class: colonClass }).range(from + index, from + index + 1));
            }

            // If this directive arguments/body is not closed, continue.
            if (depth > 0) continue;

            // Highlight opening and closing parenthesis/brace.
            decorators.push(Decoration.mark({ class: bracketClass }).range(from + position, from + position + 1));
            decorators.push(Decoration.mark({ class: bracketClass }).range(from + index, from + index + 1));

            return decorators;
        }

        return [];
    }
}